import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Shield, Check, Lightbulb, CheckCircle2, BarChart3, TrendingDown } from "lucide-react";

const StressWidget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const tabs = t("tabs", { returnObjects: true }) as any[];
    const stressTab = tabs.find((tab) => tab.id === "stress");
    const widget = stressTab?.widget || {};

    return (
        <motion.div
            className="relative w-full"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        >
            {/* Main widget */}
            <motion.div
                className="bg-gradient-to-br from-emerald-50 via-white to-emerald-50 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-emerald-100 mb-3 overflow-hidden relative"
                whileHover={{ boxShadow: "0 10px 25px -5px rgba(16, 185, 129, 0.2)" }}
                transition={{ duration: 0.3 }}
            >
                {/* Background effects */}
                <div className={`absolute ${isRTL ? "left-10" : "right-10"} -top-10 w-40 h-40 bg-gradient-to-br from-emerald-200 to-emerald-100 rounded-full opacity-20 blur-2xl`}></div>
                <div className={`absolute ${isRTL ? "right-10" : "left-10"} -bottom-10 w-40 h-40 bg-gradient-to-br from-teal-200 to-emerald-100 rounded-full opacity-20 blur-2xl`}></div>

                <div className="flex justify-between items-center mb-3 relative z-10">
                    <h5 className="text-emerald-800 font-medium flex items-center">
                        <Shield className={`w-5 h-5 ${isRTL ? "ml-1" : "mr-1"} text-emerald-600`} />
                        {widget.title}
                    </h5>
                    <motion.span
                        className="text-xs bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-3 py-1 rounded-full font-medium"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.4 }}
                    >
                        {widget.level}
                    </motion.span>
                </div>

                {/* Stress level display */}
                <div className="relative h-36 mb-4 overflow-hidden rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/10 to-teal-500/10"></div>

                    {/* Circular display */}
                    <div className="relative">
                        <svg width="102" height="112" viewBox="0 0 120 120">
                            <circle cx="60" cy="60" r="54" fill="none" stroke="#e5e7eb" strokeWidth="12" />

                            <defs>
                                <linearGradient id="stress-circle-gradient" x1="0" y1="0" x2="0" y2="120">
                                    <stop offset="0%" stopColor="#10b981" />
                                    <stop offset="100%" stopColor="#34d399" />
                                </linearGradient>
                            </defs>

                            <motion.circle
                                cx="60"
                                cy="60"
                                r="54"
                                fill="none"
                                stroke="url(#stress-circle-gradient)"
                                strokeWidth="12"
                                strokeDasharray="339.3"
                                strokeDashoffset="254.5"
                                initial={{ strokeDashoffset: 339.3 }}
                                animate={{ strokeDashoffset: 254.5 }}
                                transition={{ duration: 1.8, ease: "easeInOut" }}
                            />

                            {/* Moving indicator */}
                            <motion.g initial={{ rotate: 0 }} animate={{ rotate: 75 }} transition={{ duration: 1.8, ease: "easeInOut" }} style={{ transformOrigin: "60px 60px" }}>
                                <circle cx="60" cy="6" r="6" fill="url(#stress-circle-gradient)" />
                                <motion.circle
                                    cx="60"
                                    cy="6"
                                    r="10"
                                    fill="#10b981"
                                    opacity="0.3"
                                    animate={{ opacity: [0.3, 0.6, 0.3], r: [8, 12, 8] }}
                                    transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
                                />
                            </motion.g>
                        </svg>

                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <motion.span
                                className="text-3xl font-bold bg-gradient-to-r from-emerald-500 to-emerald-700 bg-clip-text text-transparent"
                                initial={{ scale: 0.5, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.5, duration: 0.6 }}
                            >
                                {widget.percentage}
                            </motion.span>
                            <motion.span className="text-xs text-emerald-600" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
                                {widget.stressLabel}
                            </motion.span>
                        </div>
                    </div>

                    {/* Stress level indicators */}
                    <div className={`flex gap-1 absolute top-2 ${isRTL ? "right-1" : "left-1"} items-center space-x-1 bg-white/40 backdrop-blur-sm px-2 py-1 rounded-full`}>
                        <motion.div className="flex items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                            <span className="inline-block w-2 h-2 rounded-full bg-green-500 ml-0.5"></span>
                            <span className="text-[8px] text-green-700">{widget.levels?.low}</span>
                        </motion.div>
                        <motion.div className="flex items-center opacity-40" initial={{ opacity: 0 }} animate={{ opacity: 0.4 }} transition={{ delay: 0.4 }}>
                            <span className="inline-block w-2 h-2 rounded-full bg-yellow-500 ml-0.5"></span>
                            <span className="text-[8px] text-yellow-700">{widget.levels?.medium}</span>
                        </motion.div>
                        <motion.div className="flex items-center opacity-40" initial={{ opacity: 0 }} animate={{ opacity: 0.4 }} transition={{ delay: 0.5 }}>
                            <span className="inline-block w-2 h-2 rounded-full bg-red-500 ml-0.5"></span>
                            <span className="text-[8px] text-red-700">{widget.levels?.high}</span>
                        </motion.div>
                    </div>

                    {/* Recording indicator */}
                    <motion.div className={`absolute top-2 ${isRTL ? "left-2" : "right-2"} flex items-center`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
                        <motion.div className="w-2 h-2 rounded-full bg-green-500 ml-1" animate={{ opacity: [1, 0.3, 1], scale: [1, 0.8, 1] }} transition={{ duration: 2, repeat: Infinity }} />
                        <span className="text-[9px] text-gray-500">{widget.recording}</span>
                    </motion.div>
                </div>

                {/* Status and comparison */}
                <div className="flex justify-between items-center mb-2 relative z-10">
                    <div className="flex items-center">
                        <span className="text-sm text-gray-600 ml-1">{widget.statusLabel}</span>
                        <motion.span className="text-sm font-medium text-green-600 flex items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
                            {widget.statusValue}
                            <Check className="w-4 h-4 ml-1 text-green-600" />
                        </motion.span>
                    </div>
                    <motion.div className="text-xs text-gray-500 flex items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
                        <span className="text-emerald-600 ml-1">{widget.comparison?.value}</span>
                        <span>{widget.comparison?.label}</span>
                    </motion.div>
                </div>
            </motion.div>

            {/* Sub-widgets */}
            <div className="grid grid-cols-2 gap-3 mb-3">
                {/* Weekly trend */}
                <motion.div
                    className="bg-gradient-to-br from-white to-emerald-50 backdrop-blur-sm rounded-xl p-3 shadow-sm border border-emerald-100 relative overflow-hidden"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    whileHover={{ boxShadow: "0 8px 20px -5px rgba(16, 185, 129, 0.15)", y: -2 }}
                >
                    <div className={`absolute ${isRTL ? "right-6" : "left-6"} -bottom-6 w-20 h-20 bg-emerald-200/20 rounded-full blur-xl`}></div>

                    <div className="flex items-center justify-between mb-2 relative z-10">
                        <div className="flex items-center">
                            <BarChart3 className={`w-4 h-4 text-emerald-600 ${isRTL ? "mr-1.5" : "ml-1.5"}`} />
                            <span className="text-xs text-gray-600 font-medium">{widget.weeklyTrend?.label}</span>
                        </div>
                        <TrendingDown className="w-3 h-3 text-green-500" />
                    </div>

                    {/* Weekly chart */}
                    <div className="relative h-16 flex items-end justify-between px-1">
                        {[30, 45, 60, 40, 25, 35, 20].map((value, index) => (
                            <motion.div key={index} className="relative group">
                                <motion.div
                                    className={`w-3 rounded-t-md ${
                                        value < 30
                                            ? "bg-gradient-to-t from-green-400 to-green-300"
                                            : value < 50
                                            ? "bg-gradient-to-t from-yellow-400 to-yellow-300"
                                            : "bg-gradient-to-t from-red-400 to-red-300"
                                    }`}
                                    style={{ height: `${value}%` }}
                                    initial={{ height: 0 }}
                                    animate={{ height: `${value}%` }}
                                    transition={{ duration: 0.7, delay: 0.1 * index, ease: "easeOut" }}
                                    whileHover={{ scale: 1.1 }}
                                >
                                    <div className="absolute inset-x-0 top-0 h-1/2 bg-white/30 rounded-t-md"></div>
                                </motion.div>

                                {/* Tooltip on hover */}
                                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-[8px] px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                    {value}%
                                </div>
                            </motion.div>
                        ))}

                        <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-200" style={{ bottom: "30%" }}></div>

                        <div className="absolute bottom-0 left-0 right-0 flex justify-between text-[7px] text-gray-500 pt-1">
                            {widget.weekDaysShort?.map((day: string, index: number) => (
                                <span key={index} className="bg-white/50 px-0.5 rounded">
                                    {day}
                                </span>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Stress factors */}
                <motion.div
                    className="bg-gradient-to-br from-white to-emerald-50 backdrop-blur-sm rounded-xl p-3 shadow-sm border border-emerald-100 relative overflow-hidden"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    whileHover={{ boxShadow: "0 8px 20px -5px rgba(16, 185, 129, 0.15)", y: -2 }}
                >
                    <div className={`absolute ${isRTL ? "left-6" : "right-6"} -bottom-6 w-20 h-20 bg-emerald-200/20 rounded-full blur-xl`}></div>

                    <div className="flex items-center justify-between mb-2 relative z-10">
                        <span className="text-xs text-gray-600 font-medium">{widget.factors?.label}</span>
                    </div>

                    {/* Factors display */}
                    <div className="relative h-16">
                        <div className="flex flex-col space-y-2">
                            <div className="flex items-center group">
                                <span className="text-[8px] text-gray-600 w-10">{widget.factors?.work}</span>
                                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                                    <motion.div
                                        className="h-full bg-gradient-to-r from-yellow-300 to-yellow-400 relative"
                                        style={{ width: "60%" }}
                                        initial={{ width: 0 }}
                                        animate={{ width: "60%" }}
                                        transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
                                        whileHover={{ width: "63%" }}
                                    >
                                        <div className="absolute inset-0 bg-white/30 bg-gradient-to-b from-white/40 to-transparent"></div>
                                    </motion.div>
                                </div>
                                <span className="text-[8px] text-yellow-600 mr-1 opacity-0 group-hover:opacity-100 transition-opacity">60%</span>
                            </div>
                            <div className="flex items-center group">
                                <span className="text-[8px] text-gray-600 w-10">{widget.factors?.sleep}</span>
                                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                                    <motion.div
                                        className="h-full bg-gradient-to-r from-green-300 to-green-400 relative"
                                        style={{ width: "20%" }}
                                        initial={{ width: 0 }}
                                        animate={{ width: "20%" }}
                                        transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
                                        whileHover={{ width: "23%" }}
                                    >
                                        <div className="absolute inset-0 bg-white/30 bg-gradient-to-b from-white/40 to-transparent"></div>
                                    </motion.div>
                                </div>
                                <span className="text-[8px] text-green-600 mr-1 opacity-0 group-hover:opacity-100 transition-opacity">20%</span>
                            </div>
                            <div className="flex items-center group">
                                <span className="text-[8px] text-gray-600 w-10">{widget.factors?.nutrition}</span>
                                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                                    <motion.div
                                        className="h-full bg-gradient-to-r from-green-300 to-green-400 relative"
                                        style={{ width: "15%" }}
                                        initial={{ width: 0 }}
                                        animate={{ width: "15%" }}
                                        transition={{ duration: 1, delay: 0.7, ease: "easeOut" }}
                                        whileHover={{ width: "18%" }}
                                    >
                                        <div className="absolute inset-0 bg-white/30 bg-gradient-to-b from-white/40 to-transparent"></div>
                                    </motion.div>
                                </div>
                                <span className="text-[8px] text-green-600 mr-1 opacity-0 group-hover:opacity-100 transition-opacity">15%</span>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Tips */}
            <motion.div
                className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-3 text-sm text-emerald-800 border border-emerald-100 shadow-sm relative overflow-hidden"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                whileHover={{ boxShadow: "0 8px 20px -5px rgba(16, 185, 129, 0.15)" }}
            >
                <div className={`absolute ${isRTL ? "right-10" : "left-10"} -bottom-10 w-40 h-40 bg-emerald-200/20 rounded-full blur-2xl`}></div>

                <div className="flex items-start mb-2 relative z-10">
                    <div className="bg-emerald-100 p-1.5 rounded-full ml-2">
                        <Lightbulb className="w-4 h-4 text-emerald-600" />
                    </div>
                    <h6 className="font-medium text-emerald-800 mt-0.5">{widget.tips?.title}</h6>
                </div>

                <div className="space-y-2 relative z-10">
                    {widget.tips?.items?.map((item: string, index: number) => (
                        <motion.p key={index} className="flex items-start text-xs" initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.1 + index * 0.2 }}>
                            <CheckCircle2 className={`w-3 h-3 text-emerald-500 mt-0.5 ${isRTL ? "mr-1.5" : "ml-1.5"} flex-shrink-0`} />
                            {item}
                        </motion.p>
                    ))}
                </div>
            </motion.div>
        </motion.div>
    );
};

export default StressWidget;
